import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import crawlspaceWallCodeTemplate from "features/Model/Enclosure/Templates/crawlspaceWallCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import EmbodiedCarbonFramingGroupGrade, {
    EmbodiedCarbonCalculationsSummary,
    StandardEmbodiedLayerBlock,
} from "components/EmbodiedCarbonGrade";

import globalStyles from "components/globalStyles.module.scss";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const interiorValidation = getValidation("csWallInterior");
const structureValidation = getValidation("csWallStructureType");
const typeSizeValidation = getValidation("csWallComponentTypeSize");
const sheathingValidation = getValidation("csWallSheathing");
const framingSpacingValidation = getValidation("csWallFramingSpacing");
const exteriorValidation = getValidation("csWallExterior");
const ins1Validation = getValidation("csWallInsLayer1");
const ins2Validation = getValidation("csWallInsLayer2");
const studsValidation = getValidation("csWallStudsCornersInt");

const StandardPonyWallCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { ponyWallInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const ponyWallCode = useStateSelector(`${drawerAccessor}.ponyWallCode`) || {};
        const { id: crawlWallStructureId, value: crawlWallStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.structureType`) || {};
        const { id: crawlWallSpacingId, value: crawlWallSpacingValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.spacing`) || {};
        const { value: crawlWallTypeSizeValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.componentTypeSize`) || {};
        const { value: crawlWallFramingValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.framing`) || {};
        const { value: crawlWallIns1Value } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.insulationLayer1`) || {};
        const { value: crawlWallIns2Value } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.insulationLayer2`) || {};
        const { value: crawlWallInteriorValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.interior`) || {};
        const { value: crawlWallSheathingValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.sheathing`) || {};
        const { value: crawlWallExteriorValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.exterior`) || {};
        const { value: crawlWallStudsValue } =
            useStateSelector(`${drawerAccessor}.ponyWallCode.layers.studsCornerIntersection`) || {};
        const crawlWallLabel = useStateSelector(`${drawerAccessor}.ponyWallCode.label`) || "";
        const ponyWallLayers = useStateSelector(`${drawerAccessor}.ponyWallCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.ponyWallCode.layers`;

        const frameSpacingValue = crawlWallSpacingId === null ? crawlWallFramingValue : crawlWallSpacingValue;
        const crawlWallValue = `1${crawlWallStructureValue
            .toString()
            .substr(
                -1
            )}${crawlWallTypeSizeValue}${frameSpacingValue}${crawlWallIns1Value}${crawlWallIns2Value}${crawlWallInteriorValue}${crawlWallSheathingValue}${crawlWallExteriorValue}${
            crawlWallStudsValue || 0
        }`;

        const isSolid = parseFloat(crawlWallStructureId) === 4;
        const isPanel = parseFloat(crawlWallStructureId) === 5;

        const makeSpacingDefault = ["5", "6", "7", "8", "9", "A", "B", "C"].includes(crawlWallTypeSizeValue);
        const makeInsulationDefault = ["6", "7", "8", "9", "A", "B", "C"].includes(crawlWallTypeSizeValue);

        const { codeRef: editingCodeRef = null, isLibCode = false } = ponyWallInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("PonyWall", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        const handleStuctureChange = useCallback(
            ({ id, value }) => {
                change(
                    `${drawerAccessor}.ponyWallCode.layers.insulationLayer1`,
                    getFirstOption({ indKey: id, depKey: "csWallInsLayer1" })
                );
                change(
                    `${drawerAccessor}.ponyWallCode.layers.componentTypeSize`,
                    getFirstOption({ indKey: id, depKey: "csWallComponentTypeSize" })
                );

                // Use "Framing" when set to Solid, use Spacing otherwise
                if (parseFloat(id) === 4) {
                    // Set spacing to null and grab first option for framing
                    change(`${drawerAccessor}.ponyWallCode.layers.spacing`, { id: null });
                    change(
                        `${drawerAccessor}.ponyWallCode.layers.framing`,
                        getFirstOption({
                            indKey: id,
                            depKey: "csWallFramingSpacing",
                        })
                    );
                } else {
                    // Set framing to null and grab first option for spacing
                    change(`${drawerAccessor}.ponyWallCode.layers.framing`, { id: null });

                    // Only grab default spacing id if not set
                    if (crawlWallSpacingId === null) {
                        change(
                            `${drawerAccessor}.ponyWallCode.layers.spacing`,
                            getDefaultOption("csWallFramingSpacing")
                        );
                    }
                }

                // Panel
                if (parseFloat(id) === 5) {
                    change(
                        `${drawerAccessor}.ponyWallCode.layers.insulationLayer1`,
                        getDefaultOption("csWallInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.ponyWallCode.layers.insulationLayer2`,
                        getDefaultOption("csWallInsLayer2")
                    );
                    change(`${drawerAccessor}.ponyWallCode.layers.spacing`, getDefaultOption("csWallFramingSpacing"));
                    change(
                        `${drawerAccessor}.ponyWallCode.layers.studsCornerIntersection`,
                        getDefaultOption("csWallStudsCornersInt")
                    );
                }
            },
            [crawlWallStructureId, change, crawlWallSpacingId]
        );

        useEffect(() => {
            const initialValues = {
                ponyWallCode: isEmpty(ponyWallInsType) ? crawlspaceWallCodeTemplate : ponyWallInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = crawlWallValue;
        }, [crawlWallValue, crawlWallLabel]);

        // Removes floor header from opts
        const excludeValues = ["8"];
        const crawlWallStructureOpts = getOptions({ fieldKey: "csWallStructureType" }).filter(
            (opt) => !excludeValues.includes(opt.value.value)
        );

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const ponyEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"} style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.ponyWallCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={crawlWallLabel.toString() === valueRef.current ? crawlWallValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.ponyWallCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={crawlWallValue}
                    />
                    <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                        <CodeLibraryDestination
                            drawerAccessor={drawerAccessor}
                            editingCodeRef={editingCodeRef}
                            isLibCode={isLibCode}
                        />
                    </div>
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.ponyWallCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={crawlWallStructureOpts}
                            validate={structureValidation}
                            onChange={handleStuctureChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.ponyWallCode.layers.componentTypeSize`}
                            label="Component Type / Size"
                            placeholder="Choose Type / Size"
                            options={getOptions({ fieldKey: "csWallComponentTypeSize", indKey: crawlWallStructureId })}
                            validate={typeSizeValidation}
                            onChange={({ value = "" }) => {
                                if (["6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(
                                        `${drawerAccessor}.ponyWallCode.layers.insulationLayer1`,
                                        getDefaultOption("csWallInsLayer1")
                                    );
                                    change(
                                        `${drawerAccessor}.ponyWallCode.layers.insulationLayer2`,
                                        getDefaultOption("csWallInsLayer2")
                                    );
                                }
                                if (["5", "6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(`${drawerAccessor}.ponyWallCode.layers.framing`, { id: 0, value: "0" });
                                }
                            }}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        {isSolid ? (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.ponyWallCode.layers.framing`}
                                label="Framing"
                                placeholder="Choose Framing"
                                options={getOptions({ fieldKey: "csWallFramingSpacing", indKey: crawlWallStructureId })}
                                validate={framingSpacingValidation}
                                disabled={makeSpacingDefault}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        ) : (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.ponyWallCode.layers.spacing`}
                                label="Spacing"
                                placeholder="Choose Spacing"
                                options={getOptions({ fieldKey: "csWallFramingSpacing", indKey: crawlWallStructureId })}
                                validate={framingSpacingValidation}
                                disabled={isPanel}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        )}
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.ponyWallCode.layers.studsCornerIntersection`}
                            label="Studs at Corners and Intersections"
                            placeholder="Choose"
                            options={getOptions({ fieldKey: "csWallStudsCornersInt" })}
                            validate={studsValidation}
                            disabled={isSolid || isPanel}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={ponyWallLayers}
                        componentType="crawlspaceWall"
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={ponyWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ponyEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ponyWallCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csWallInsLayer1", indKey: crawlWallStructureId })}
                        validate={ins1Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={ponyWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ponyEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ponyWallCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csWallInsLayer2" })}
                        validate={ins2Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interior"
                    layers={ponyWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ponyEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ponyWallCode.layers.interior`}
                        label="Interior"
                        placeholder="Choose Interior"
                        options={getOptions({ fieldKey: "csWallInterior" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="sheathing"
                    layers={ponyWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={ponyEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ponyWallCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csWallSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="exterior"
                    layers={ponyWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...ponyEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.ponyWallCode.layers.exterior`}
                        label="Exterior"
                        placeholder="Choose Exterior"
                        options={getOptions({ fieldKey: "csWallExterior" })}
                        validate={exteriorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={ponyWallCode}
                    componentType={"ponyWall"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.ponyWallCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "ponyWallInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const { ponyWallCode = {}, addToLibrary = false, orgLibraryId = "", selectedComponents = [] } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        standardCodeLogic({
            change,
            componentCodeType: "CrawlspaceWall",
            componentId,
            code: ponyWallCode,
            inputName: "ponyWallInsType",
            codeType: "S",
            isCodeLibrary: false,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardPonyWallCode);
